// src/components/LoginButtons.js
import React, { useState } from "react";
import { auth } from "../../firebase";
import { signOut } from "firebase/auth";
import { useAuth } from "../../context/AuthContext";
import {
  IconButton,
  Menu,
  MenuItem,
  Box,
  Tooltip,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import BedtimeOutlinedIcon from "@mui/icons-material/BedtimeOutlined";
import ParkOutlinedIcon from "@mui/icons-material/ParkOutlined";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useChild } from "../../context/ChildContext";

const LoginButtons = () => {
  const { currentChild, childList } = useChild();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      window.location.href = `/`;
    } catch (error) {
      console.error("Error signing out:", error);
    } finally {
      handleMenuClose();
    }
  };

  const handleSelect = (childId) => {
    window.location.href = `/tracker/${childId}`;
  };

  return (
    <Box
      sx={{
        position: "absolute",
        top: 24,
        right: 16,
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        gap: 0.5,
      }}
    >
      {user && (
        <>
          <Tooltip title="Account options">
            <IconButton
              onClick={handleMenuOpen}
              size="small"
              sx={{ color: "white" }}
              aria-controls={Boolean(anchorEl) ? "account-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={Boolean(anchorEl) ? "true" : undefined}
            >
              <AccountCircleOutlinedIcon />
            </IconButton>
          </Tooltip>
          <Menu
            id="account-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            {childList.map((child) => (
              <MenuItem
                key={child.id}
                onClick={() => handleSelect(child.id)}
                selected={child.id === currentChild?.id}
              >
                <ListItemIcon>
                  <PersonOutlineOutlinedIcon />
                </ListItemIcon>
                <ListItemText> {child.name}</ListItemText>
              </MenuItem>
            ))}
            {childList.length > 1 && <Divider />}
            {childList.length > 1 && (
              <MenuItem
                onClick={() => {
                  navigate("/sleep-overview");
                  handleMenuClose();
                }}
              >
                <ListItemIcon>
                  <BedtimeOutlinedIcon />
                </ListItemIcon>
                <ListItemText>{t("nav.sleep_overview")}</ListItemText>
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                navigate("/playgrounds");
                handleMenuClose();
              }}
            >
              <ListItemIcon>
                <ParkOutlinedIcon />
              </ListItemIcon>
              <ListItemText>{t("nav.playgrounds")}</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                navigate("/settings");
                handleMenuClose();
              }}
            >
              <ListItemIcon>
                <SettingsOutlinedIcon />
              </ListItemIcon>
              <ListItemText>{t("nav.settings")}</ListItemText>
            </MenuItem>
            <Divider />
            <a
              href="https://www.sst.dk/-/media/Udgivelser/2021/Boernesundhed/SundeBoern_tilgaengelig.ashx?sc_lang=da&hash=3A7DB5CFAFBDBFA1C507950FE21F7EF6"
              download
              style={{ textDecoration: "none", color: "inherit" }}
            >
              <MenuItem>
                <ListItemIcon>
                  <PictureAsPdfIcon />
                </ListItemIcon>
                <ListItemText>{t("nav.healthy_children_pdf")}</ListItemText>
              </MenuItem>
            </a>
            <Divider />

            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText>{t("nav.logout")}</ListItemText>
            </MenuItem>
          </Menu>
        </>
      )}
    </Box>
  );
};

export default LoginButtons;
